import { type ReactNode } from 'react'
import { delta as fmtDelta, inteiro, pct } from '../../lib/formato'
import { useLargura } from './base'

/**
 * O cartão de KPI — valor, delta e minigráfico.
 *
 * Aparece em cinco das quinze referências do Lucas (2, 5, 6, 7, 10) e
 * é a peça que falta para o Clinuma ter uma tela de "Início".
 *
 * Três decisões que separam este cartão de um número dentro de uma
 * caixa:
 *
 * 1. **`sentido` é obrigatório quando há delta.** Subir é bom em
 *    receita e é péssimo em faltas. O `Grafico.tsx` de hoje cravou
 *    `melhorou = ultimo < primeiro` (linha 74) — certo para dor,
 *    errado para amplitude de movimento e para força, que são as
 *    outras medidas que a mesma tela desenha. Aqui quem chama diz.
 *
 * 2. **O delta nunca é só cor.** Seta + sinal + o período por escrito
 *    ("vs. julho"). Delta verde sem seta é ilegível para 8% dos
 *    homens, e "+5%" sem período comparado não é informação.
 *
 * 3. **O minigráfico é decoração honesta.** `aria-hidden`, sem eixo,
 *    sem número: ele responde "vinha subindo?" e nada mais. Quem
 *    precisa dos pontos abre o gráfico grande. Um minigráfico com
 *    eixo é um gráfico grande espremido, e não se lê.
 *
 * Anatomia (de cima para baixo):
 *     rótulo        12px, --cl-viz-rotulo, capitalização de frase
 *     valor         1,75rem, 660, figuras PROPORCIONAIS
 *     delta         12px, pílula, seta + sinal + período
 *     minigráfico   12 pontos, 28px de altura, contexto cinza +
 *                   último trecho na cor da série
 *     meta          opcional: medidor fino + "30 de 40"
 */

export type Sentido = 'cima' | 'baixo' | 'neutro'

export function Kpi({
  rotulo, valor, ajuda,
  delta, periodo, sentido = 'neutro',
  serie, meta, acao, carregando = false,
}: {
  /** Capitalização de frase, sem dois-pontos. "Atendimentos hoje" */
  rotulo: string
  /** Já formatado por `lib/formato`. O cartão não sabe de centavos. */
  valor: string
  ajuda?: string
  /** Variação percentual contra `periodo`. */
  delta?: number | null
  /** "vs. mês anterior", "vs. mesma semana" — sempre nomeado. */
  periodo?: string
  /** Para onde é bom ir. Faltas = 'baixo'. Receita = 'cima'. */
  sentido?: Sentido
  /** Até 12 pontos, do mais antigo ao mais recente. */
  serie?: number[] | null
  meta?: { alvo: number; realizado: number; unidade?: string } | null
  acao?: ReactNode
  carregando?: boolean
}) {
  if (carregando) {
    // Esqueleto com a ALTURA do cartão pronto. Esqueleto de altura
    // diferente faz a linha inteira pular quando o dado chega.
    return <div className="kpi kpi--esqueleto esqueleto" aria-hidden="true" />
  }

  const bom = delta == null || delta === 0 || sentido === 'neutro' ? null
    : sentido === 'cima' ? delta > 0 : delta < 0
  const tomDelta = bom === null ? 'neutro' : bom ? 'bom' : 'ruim'
  const seta = delta == null || delta === 0 ? '→' : delta > 0 ? '↑' : '↓'

  return (
    <article className="kpi cartao">
      <p className="kpi__rotulo">{rotulo}</p>

      <p className="kpi__valor">{valor}</p>

      {delta != null && (
        <p className={`kpi__delta kpi__delta--${tomDelta}`}>
          <span aria-hidden="true">{seta}</span>
          <span>{fmtDelta(delta)}</span>
          {periodo && <span className="kpi__periodo">{periodo}</span>}
        </p>
      )}

      {ajuda && <p className="kpi__ajuda">{ajuda}</p>}

      {serie && serie.length > 1 && <Minigrafico pontos={serie.slice(-12)} />}

      {meta && (
        <div className="kpi__meta">
          <div className="kpi__medidor">
            <span style={{
              width: `${Math.min(100, meta.alvo > 0 ? (meta.realizado / meta.alvo) * 100 : 0)}%`,
            }} />
          </div>
          <span className="kpi__meta-texto">
            {inteiro(meta.realizado)} de {inteiro(meta.alvo)}
            {meta.unidade ? ` ${meta.unidade}` : ''}
            {' · '}
            {pct(meta.alvo > 0 ? (meta.realizado / meta.alvo) * 100 : 0)}
          </span>
        </div>
      )}

      {acao && <div className="kpi__acao">{acao}</div>}
    </article>
  )
}

/**
 * Minigráfico de 12 pontos.
 *
 * A escala é dos DADOS com 8% de folga, não do zero: aqui o assunto é
 * a forma da curva, não a magnitude — para magnitude existe o valor
 * grande logo acima. O último trecho ganha a cor da série e um ponto
 * com anel da superfície, porque é o "agora".
 */
function Minigrafico({ pontos, altura = 28 }: { pontos: number[]; altura?: number }) {
  // Medido em pixel de verdade. `preserveAspectRatio="none"` seria
  // menos código e esmagaria o ponto final numa elipse.
  const [caixa, L] = useLargura<HTMLDivElement>()
  const n = pontos.length
  const min = Math.min(...pontos), max = Math.max(...pontos)
  const folga = (max - min) * 0.08 || 1
  const y0 = min - folga, y1 = max + folga
  const px = (i: number) => 4 + (i / (n - 1)) * Math.max(0, L - 8)
  const py = (v: number) => altura - 4 - ((v - y0) / (y1 - y0 || 1)) * (altura - 8)

  const d = pontos.map((v, i) => `${i ? 'L' : 'M'}${px(i).toFixed(1)},${py(v).toFixed(1)}`).join(' ')
  const ultimoTrecho =
    `M${px(n - 2).toFixed(1)},${py(pontos[n - 2]!).toFixed(1)}`
    + ` L${px(n - 1).toFixed(1)},${py(pontos[n - 1]!).toFixed(1)}`

  return (
    // aria-hidden: o número já está escrito no cartão, e uma segunda
    // leitura da mesma coisa atrapalha quem usa leitor de tela.
    <div className="kpi__spark" ref={caixa} aria-hidden="true">
      {L > 0 && (
        <svg width={L} height={altura} focusable="false">
          <path d={d} fill="none" stroke="var(--cl-viz-contexto)"
                strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
          <path d={ultimoTrecho} fill="none" stroke="var(--cl-viz-serie-1)"
                strokeWidth="2" strokeLinecap="round" />
          <circle cx={px(n - 1)} cy={py(pontos[n - 1]!)} r="3.5"
                  fill="var(--cl-viz-serie-1)"
                  stroke="var(--cl-viz-superficie)" strokeWidth="2" />
        </svg>
      )}
    </div>
  )
}

/** A fileira de KPIs. Quatro no desktop, dois no tablet, um no celular. */
export function LinhaDeKpis({ children }: { children: ReactNode }) {
  return <div className="kpis">{children}</div>
}
